import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Users, Heart, Sparkles, ExternalLink, Lock } from 'lucide-react';

// New logo image URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/mnjq1cak_Recovery%20Room%20Archive-07.png";

const LoginPage = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const [loginData, setLoginData] = useState({ email: '', password: '' });

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await login(loginData.email, loginData.password);
            toast.success('Welcome back to the Recovery Room!');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Login failed');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-[#3D4435]">
            {/* Left side - Image/Branding */}
            <div 
                className="hidden lg:flex lg:w-1/2 relative"
                style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1631127190147-7ac5488fe842?crop=entropy&cs=srgb&fm=jpg&q=85)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-br from-[#3D4435]/95 to-[#3D4435]/80" />
                <div className="relative z-10 flex flex-col justify-center px-12 text-white">
                    <div className="flex items-center gap-4 mb-8">
                        <img src={LOGO_URL} alt="Recovery Room Logo" className="w-16 h-16 object-contain" />
                    </div>
                    <h1 className="text-5xl font-bold mb-6 font-['Playfair_Display']">Recovery Room</h1>
                    <p className="text-xl mb-8 text-[#A0A0A0] leading-relaxed">
                        Admin Portal for managing the brotherhood, events, and community initiatives.
                    </p>
                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#C9A227]/20 flex items-center justify-center">
                                <Users className="w-5 h-5 text-[#C9A227]" />
                            </div>
                            <span className="text-[#A0A0A0]">Manage member profiles and applications</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#C9A227]/20 flex items-center justify-center">
                                <Heart className="w-5 h-5 text-[#C9A227]" />
                            </div>
                            <span className="text-[#A0A0A0]">Track events and RSVP attendance</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#C9A227]/20 flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-[#C9A227]" />
                            </div>
                            <span className="text-[#A0A0A0]">Celebrate and spotlight community members</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right side - Login Form */}
            <div className="flex-1 flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    {/* Mobile header */}
                    <div className="text-center mb-8 lg:hidden">
                        <div className="flex justify-center mb-4">
                            <img src={LOGO_URL} alt="Recovery Room Logo" className="w-12 h-12 object-contain" />
                        </div>
                        <h1 className="text-3xl font-bold text-white font-['Playfair_Display']">Recovery Room</h1>
                        <p className="text-[#666] mt-2">Admin Portal</p>
                    </div>

                    {/* Back to main site link */}
                    <div className="mb-6 text-center">
                        <a
                            href="/join"
                            className="inline-flex items-center gap-2 text-[#C9A227] hover:text-[#E8D48A] text-sm"
                            data-testid="back-to-site-login"
                        >
                            <ExternalLink className="w-4 h-4" />
                            Back to Main Site
                        </a>
                    </div>

                    <Card className="bg-[#4A5240] border-[#5A6350] shadow-2xl">
                        <CardHeader className="text-center pb-2">
                            <div className="flex justify-center mb-4">
                                <div className="w-14 h-14 rounded-full bg-[#C9A227]/20 flex items-center justify-center">
                                    <Lock className="w-7 h-7 text-[#C9A227]" />
                                </div>
                            </div>
                            <CardTitle className="text-2xl font-bold text-white font-['Playfair_Display']">
                                Admin Sign In
                            </CardTitle>
                            <CardDescription className="text-[#A0A0A0]">
                                Sign in to manage the Recovery Room community
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <form onSubmit={handleLogin} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="login-email" className="text-white">Email</Label>
                                    <Input
                                        id="login-email"
                                        type="email"
                                        placeholder="admin@recoveryroom.org"
                                        value={loginData.email}
                                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                                        required
                                        className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227]"
                                        data-testid="login-email-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="login-password" className="text-white">Password</Label>
                                    <Input
                                        id="login-password"
                                        type="password"
                                        placeholder="Enter your password"
                                        value={loginData.password}
                                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                                        required
                                        className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227]"
                                        data-testid="login-password-input"
                                    />
                                </div>
                                <Button 
                                    type="submit" 
                                    className="w-full bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold"
                                    disabled={isLoading}
                                    data-testid="login-submit-btn"
                                >
                                    {isLoading ? 'Signing in...' : 'Sign In'}
                                </Button>
                            </form>

                            <div className="mt-6 pt-6 border-t border-[#5A6350] text-center">
                                <p className="text-[#A0A0A0] text-sm">
                                    Need admin access? Contact an existing administrator.
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    <p className="text-center text-sm text-[#666] mt-6">
                        Recovery Room Admin Portal &copy; 2026
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
